import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import _cloneDeep from 'lodash.clonedeep'

import { updateModalActionRef, setInitialActionDetail, deleteActionDetail, saveActionDetail, updateActionsErrors, updateModalError as updateDetailsModalError } from 'redux/smartchat/details/actions'
import { setAlternativesInitialActionDetail, saveAlternativesActionDetail, updateAlternativesModalActionRef, deleteAlternativesModalActionRef, deleteAlternativesActionDetail, updateAlternativesActionsErrors, deleteAlternativesActionsErrors, updateModalError as updateAlternativesModalError } from 'redux/smartchat/alternatives/actions'
import ActionModalUI from './ActionModalUI'
import { getActionsOptions, getActionsModalLegend, getParsedActionDetail, typesData } from 'components/SmartChatView/utils'

const ActionModal = ({ isOpen, toggle, dataWay }) => {
  const { header, modalActionRef, actionDetail, processDetail, actionsErrors, modalLoading, modalError } = useSelector(store => ({
    header: store.smartchatHeader.header,
    modalActionRef: dataWay?.type === 'alternatives'
      ? store.smartchatAlternatives.alternativesModalActionRef.find(modalActionRef => modalActionRef.alternativesId === dataWay.alternativesId) || {}
      : store.smartchatDetails.modalActionRef,
    actionDetail: dataWay?.type === 'alternatives'
      ? store.smartchatAlternatives.alternativesActionDetail.find(actionDetail => actionDetail.alternativesId === dataWay.alternativesId)?.actionDetail || {}
      : store.smartchatDetails.actionDetail,
    processDetail: store.smartchatDetails.processDetail,
    actionsErrors: dataWay?.type === 'alternatives'
      ? store.smartchatAlternatives.alternativesActionsErrors.find(actionsErrors => actionsErrors.alternativesId === dataWay.alternativesId) || {}
      : store.smartchatDetails.actionsErrors,
    modalLoading: store.smartchatDetails.modalLoading,
    modalError: dataWay?.type === 'alternatives'
      ? store.smartchatAlternatives.modalError
      : store.smartchatDetails.modalError
  }), shallowEqual)
  const dispatch = useDispatch()
  const [actionTypeValue, setActionTypeValue] = useState(null)
  const [actionValue, setActionValue] = useState(null)
  const actionTypeOptions = [
    { value: 'assistance', label: I18n.t('smartchat_actions_assistance') },
    { value: 'transactional', label: I18n.t('smartchat_actions_transactional') }
  ]
  const actionsOptions = getActionsOptions({
    rawActions: header.assistant_actions_control,
    actionType: modalActionRef.actionType,
    containerType: modalActionRef.containerType,
    fromActionType: modalActionRef.fromActionType,
    fromAction: modalActionRef.fromAction
  }) || []

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) {
      if (modalActionRef.mode === 'add') {
        if (processDetail.processType !== 'transactional') onChangeActionTypeValue(actionTypeOptions[0])
      }

      if (modalActionRef.mode === 'update') {
        if (['customerAction', 'operatorAction'].includes(modalActionRef.actionType)) {
          onChangeActionTypeValue(actionTypeOptions[0])
        }
        if (['transactionalAction', 'backendAction'].includes(modalActionRef.actionType)) {
          onChangeActionTypeValue(actionTypeOptions[1])
        }
      }
    }

    return () => {
      if (dataWay?.type === 'alternatives') {
        dispatch(deleteAlternativesActionsErrors({ alternativesId: dataWay.alternativesId }))
        dispatch(deleteAlternativesActionDetail({ alternativesId: dataWay.alternativesId }))
        dispatch(deleteAlternativesModalActionRef({ alternativesId: dataWay.alternativesId }))
      } else {
        dispatch(updateActionsErrors({}))
        dispatch(deleteActionDetail())
        dispatch(updateModalActionRef({}))
      }

      isSubscribed = false
    }
  }, [])

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed && actionsOptions.length && modalActionRef.mode === 'update') {
      let option = null

      if (modalActionRef.actionType === 'botAction') {
        option = actionsOptions.find(action => action.value === getParsedActionDetail({ mode: 'revert', actionNameType: actionDetail.eventType, actionDetail }).eventType)
      }
      if (typesData.clientOperatorActionTypes.includes(modalActionRef.actionType)) {
        option = actionsOptions.find(action => action.value === getParsedActionDetail({ mode: 'revert', actionNameType: actionDetail.actionSubtype, actionDetail }).eventType)
      }

      if (option) setActionValue(option)
    }

    return () => isSubscribed = false
  }, [actionsOptions.length])

  const onChangeActionTypeValue = (actionType) => {
    // Errors cleanup
    const _actionsErrors = _cloneDeep(actionsErrors)
    delete _actionsErrors.actionType
    if (dataWay?.type === 'alternatives') {
      dispatch(updateAlternativesActionsErrors({ alternativesId: dataWay.alternativesId, actionsErrors: _actionsErrors }))
      if (modalError) dispatch(updateAlternativesModalError(null))
    } else {
      dispatch(updateActionsErrors(_actionsErrors))
      if (modalError) dispatch(updateDetailsModalError(null))
    }

    const _modalActionRef = _cloneDeep(modalActionRef)
    if (_modalActionRef.type === 'customer') {
      if (actionType.value === 'assistance') _modalActionRef.actionType = 'customerAction'
      if (actionType.value === 'transactional') _modalActionRef.actionType = 'transactionalAction'
    }
    if (_modalActionRef.type === 'operator') {
      if (actionType.value === 'assistance') _modalActionRef.actionType = 'operatorAction'
      if (actionType.value === 'transactional') _modalActionRef.actionType = 'backendAction'
    }
    if (dataWay?.type === 'alternatives') {
      dispatch(updateAlternativesModalActionRef({ alternativesId: dataWay.alternativesId, modalActionRef: _modalActionRef }))
    } else {
      dispatch(updateModalActionRef(_modalActionRef))
    }

    setActionTypeValue(actionType)
  }

  const onChangeActionValue = (action) => {
    setActionValue(action)

    // Prepara los parámetros iniciales de la acción
    if (!dataWay) {
      dispatch(setInitialActionDetail(getParsedActionDetail({ mode: 'convert', actionNameType: action.value })))
    } else {
      dataWay?.type === 'alternatives' && dispatch(setAlternativesInitialActionDetail({ alternativesId: dataWay.alternativesId, ...getParsedActionDetail({ mode: 'convert', actionNameType: action.value }) }))
    }
  }

  const handleSaveAction = () => {
    if (!dataWay) {
      dispatch(saveActionDetail({ onFinish: toggle }))
    } else {
      dataWay?.type === 'alternatives' && dispatch(saveAlternativesActionDetail({ alternativesId: dataWay.alternativesId, onFinish: toggle }))
    }
  }

  return (
    <ActionModalUI
      mode={modalActionRef.mode}
      isOpen={isOpen}
      toggle={toggle}
      legend={I18n.t(getActionsModalLegend({ type: modalActionRef.type, mode: modalActionRef.mode }))}
      modalActionRef={modalActionRef}
      actionDetail={actionDetail}
      processDetail={processDetail}
      dataWay={dataWay}
      actionTypeOptions={actionTypeOptions}
      actionTypeValue={actionTypeValue}
      onChangeActionTypeValue={onChangeActionTypeValue}
      actionsOptions={actionsOptions}
      actionValue={actionValue}
      onChangeActionValue={onChangeActionValue}
      handleSaveAction={handleSaveAction}
      actionsErrors={actionsErrors}
      modalLoading={modalLoading}
      modalError={modalError}
    />
  )
}

export default ActionModal
